import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (!token) {
      setTokenError('No reset token found. Please request a new password reset link.');
    }
  }, [token]);

  const passwordsMatch = confirmPassword === '' || newPassword === confirmPassword;
  const strongEnough = newPassword.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!strongEnough) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setDone(true);
        toast.success('Password updated! You can now sign in.');
      } else {
        toast.error(data.detail || 'Failed to reset password.');
        if (response.status === 400) {
          setTokenError(data.detail || 'Invalid or expired link.');
        }
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
            <span className="text-2xl font-heading font-extrabold">Snagcart</span>
          </div>
          <h1 className="text-4xl font-heading font-extrabold mb-2">
            {done ? 'Password Updated' : 'Set New Password'}
          </h1>
          <p className="text-zinc-400">
            {done ? 'Your password has been changed successfully.' : 'Choose a strong password for your account.'}
          </p>
        </div>

        <div className="glass p-8 rounded-2xl space-y-6">
          {/* Invalid token state */}
          {tokenError && !done && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <p className="text-red-400 font-semibold">{tokenError}</p>
              <Button
                type="button"
                onClick={() => navigate('/auth')}
                className="w-full bg-white text-black hover:bg-zinc-200 rounded-full h-12 font-bold"
              >
                Back to Sign In
              </Button>
            </div>
          )}

          {/* Success state */}
          {done && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-white font-semibold text-lg">All done!</p>
              <p className="text-zinc-400 text-sm">Your password has been updated. Sign in with your new password.</p>
              <Button
                type="button"
                onClick={() => navigate('/auth')}
                className="w-full bg-white text-black hover:bg-zinc-200 rounded-full h-12 font-bold"
              >
                Sign In
              </Button>
            </div>
          )}

          {/* Reset form */}
          {!tokenError && !done && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="new-password" className="text-white mb-2 block">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNew ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {newPassword && !strongEnough && (
                  <p className="text-red-400 text-xs mt-1">Password must be at least 8 characters.</p>
                )}
              </div>

              <div>
                <Label htmlFor="confirm-password" className="text-white mb-2 block">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repeat your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword && !passwordsMatch && (
                  <p className="text-red-400 text-xs mt-1">Passwords do not match.</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !strongEnough || !passwordsMatch || !confirmPassword}
                className="w-full bg-white text-black hover:bg-zinc-200 rounded-full h-12 font-bold transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
